import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { Importer } from '@feathr/blackbox';
import { FormSummaryItem, Pill, Skeleton } from '@feathr/components';
import { getTaggedPeopleUrl } from '@feathr/extender/App/DataExplorePage/DataExplorePage.utils';
import { useLocalUrl, useRole, useStore } from '@feathr/extender/state';

import { hasImportTag } from './ImportRunDetails.utils';

/** How many more times to ask for the import's own tag after it wasn't found. */
const OWN_TAG_RETRIES = 3;
const OWN_TAG_RETRY_DELAY = 2000;

interface IImportTagsLineProps {
  importer: Importer;
  /** How long to wait, in milliseconds, before asking again for the import's own tag. */
  retryDelay?: number;
}

/**
 * The tags the import applied to the people it imported: its own tag, then the tags picked in the
 * wizard. Each opens the people list, filtered to that tag, in a new tab, for a user who can open
 * that list. Leaves out a tag that can't be read, such as one that was deleted.
 */
function ImportTagsLine({
  importer,
  retryDelay = OWN_TAG_RETRY_DELAY,
}: Readonly<IImportTagsLineProps>): JSX.Element | null {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { Tags } = useStore();
  const { hasSegments } = useRole();
  const localUrl = useLocalUrl();
  const [retries, setRetries] = useState(0);

  const hasTags = hasImportTag(importer);
  // The API gives the import's own tag the import's id. It saves a finished import a moment before
  // it creates that tag, so a row that has just finished can ask before the tag exists.
  const ownTag = hasTags ? Tags.get(importer.id) : undefined;
  const isOwnTagMissing = !!ownTag && ownTag.isErrored && !ownTag.isPending;
  const isRetrying = isOwnTagMissing && retries < OWN_TAG_RETRIES;

  useEffect(() => {
    if (!ownTag || !isRetrying) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setRetries((count) => count + 1);
      void Tags.fetchModel(ownTag);
    }, retryDelay);
    return (): void => clearTimeout(timer);
  }, [isOwnTagMissing]);

  if (!hasTags) {
    return null;
  }

  const tagIds = Array.from(new Set([importer.id, ...importer.get('tag_ids', [])]));
  const tags = tagIds.map((id) => Tags.get(id));
  const readableTags = tags.filter((tag) => !tag.isErrored);

  let value: ReactNode = '—';
  if (isRetrying || tags.some((tag) => tag.isPending)) {
    value = <Skeleton />;
  } else if (readableTags.length) {
    value = (
      <Flex gap={theme.spacing['1']} wrap={'wrap'}>
        {readableTags.map((tag) => {
          const pill = (
            <Pill key={tag.id} theme={'neutral'}>
              {tag.name}
            </Pill>
          );
          return hasSegments ? (
            <Link
              key={tag.id}
              rel={'noopener noreferrer'}
              target={'_blank'}
              to={localUrl(getTaggedPeopleUrl(tag.id))}
            >
              {pill}
            </Link>
          ) : (
            pill
          );
        })}
      </Flex>
    );
  }

  // Placeholder copy, pending Product and Design.
  return <FormSummaryItem label={t('Tags')} value={value} />;
}

export default observer(ImportTagsLine);
